import { call, put, select } from 'redux-saga/effects';
import { setUser, setEphemeralToken } from 'app/actions/user-session';
import { auth } from 'mz-sdk';
import { findLanguage } from 'mz-intl/core/languages';
import {
  getHistory,
  appendPropagatedParameters,
  addLocaleToPath
} from 'app/history';
import { getLocation } from '../selectors';
import { redirectBack, showErrorNotification } from 'app/sagas/utils';
import { getEphemeralToken, getUser, isTravelAgent } from 'app/sagas/selectors';
import { setCookie, removeCookie } from 'app/utils/cookie';
import getUserData from './getUserData';
import doSetLocaleCurrency from './doSetLocaleCurrency';
import { REDIRECT_AFTER_LOGIN_HOOK } from 'app/constants';
import { hook } from 'app/utils/hook';
import config from 'config';
import posthog from 'posthog-js';

export default function* doLogin(action, redirect = true, skipLogin = false) {
  try {
    posthog.capture('Old Flow: User Successfully Signed In', {
      sign_in_method: 'email',
    });
    const history = yield select(getHistory);

    if (!skipLogin) {
      const { data, ephemeral_token } = yield call(auth.login, action.payload);

      if (ephemeral_token) {
        yield put(setEphemeralToken(ephemeral_token));
        yield select(getEphemeralToken);
        yield call(history.push, '/user/login/mfa');
        return;
      }
      yield put(setUser(data));
      yield call(getUserData);
    }

    // On login, we identify the user with the Chameleon api
    if (typeof chmln !== 'undefined') {
      // eslint-disable-line
      chmln.identify(window.mozioUser); // eslint-disable-line
    }

    // For Travel Agents, resolve the saved language preference and persist it to
    // a shared host cookie so transfer-next (same host) resolves it server-side,
    // and so the first post-login redirect can already carry the locale — making
    // the historical "redirect → setLocale full reload" double-navigation a no-op.
    const user = yield select(getUser);
    const isTA = yield select(isTravelAgent);
    const taLocale =
      isTA && user && user.language ? findLanguage(user.language).value : null;
    if (taLocale) {
      setCookie('ta-locale', taLocale, { days: 365 });
    } else {
      removeCookie('ta-locale');
    }

    // Redirect user to the previous point
    // or to the point requested by `next` URL parameter
    const { query } = yield select(getLocation);
    const nextUrl = query.next;
    const returnPath = query.returnPath
      ? decodeURIComponent(query.returnPath)
      : null;

    // `returnPath` is authoritative when present (relative): a single localized
    // full-reload navigation, no in-page `doSetLocaleCurrency` (the destination
    // load re-runs doInit → doSetLocaleCurrency).
    if (returnPath && !returnPath.match(/^https?:\/\//)) {
      // eslint-disable-next-line require-atomic-updates
      window.location.href = addLocaleToPath(returnPath, taLocale);
    } else if (nextUrl) {
      if (nextUrl.match(/^https?:\/\//)) {
        // External absolute `next` — cannot safely prepend a locale path
        // segment; the `ta-locale` cookie (set above) carries the preference to
        // the (same-host) destination.
        window.location.href = nextUrl; // eslint-disable-line require-atomic-updates
      } else {
        // Relative `next` is an in-page SPA navigation: there is no destination
        // `doInit` to apply locale/currency, so do it here.
        history.push(nextUrl);
        yield call(doSetLocaleCurrency);
      }
    } else if (redirect) {
      yield call(redirectBack, taLocale);
    } else {
      const redirectUrl = hook({
        name: REDIRECT_AFTER_LOGIN_HOOK,
        defaultHandler: () => '/',
      });
      const redirectUrlWithParams = appendPropagatedParameters(redirectUrl);
      // eslint-disable-next-line require-atomic-updates
      window.location.href = redirectUrlWithParams.match(/^https?:\/\//)
        ? redirectUrlWithParams
        : addLocaleToPath(redirectUrlWithParams, taLocale);
    }
  } catch (error) {
    yield put(setUser());
    yield call(showErrorNotification, {
      error,
      titleId: 'USER.LOGIN_ERROR_TITLE',
    });

    if (
      config.PASSWORD_RESET_ABILITY &&
      error.errorCode &&
      error.errorCode === 'user_password_expired'
    ) {
      const history = yield select(getHistory);
      history.push('/user/forgot-password');
    }
  }
}
